import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

export interface BriefItem {
  text: string;
  category:
    | 'demand'
    | 'email'
    | 'treatment'
    | 'records'
    | 'coverage'
    | 'agent'
    | 'task'
    | 'client'
    | 'calendar';
  matter_name: string | null;
  link_type: 'front' | 'task' | 'matter' | 'calendar' | null;
  link_id: string | null;
}

export interface BriefNarrative {
  overnight: BriefItem[] | null;
  for_you: BriefItem[];
}

export interface OverdueBreakdownItem {
  name: string;
  count: number;
  overdue_30_plus: number;
  overdue_14_plus: number;
  overdue_recent: number;
}

export interface BlockedByYouItem {
  kind: 'mention' | 'email';
  title: string;
  subtitle: string;
  who_is_waiting: string;
  waiting_since: string | null;
  task_id: string | null;
  matter_id: string | null;
}

export interface WeeklyDueItem {
  day: string;
  count: number;
  is_today: boolean;
}

export interface TaskMetrics {
  due_today: number;
  overdue: number;
  blocked: number;
  active: number;
  high_priority: number;
  completed_this_week: number;
  overdue_by_matter: OverdueBreakdownItem[];
  overdue_by_type: OverdueBreakdownItem[];
  weekly_due: WeeklyDueItem[];
  blocked_by_you: BlockedByYouItem[];
}

export interface PresidentialBrief {
  id: string;
  user_id: string;
  brief_date: string;
  narrative: BriefNarrative;
  model_used: string;
  generation_mode: string;
  generation_time_seconds: number | null;
  confidence: number;
  context_sources_used: string[];
  status: string;
  thumbs_down: boolean;
  thumbs_down_reason: string | null;
  generated_at: string;
  created_at: string;
  task_metrics?: TaskMetrics;
}

export const presidentialBriefApi = createApi({
  reducerPath: 'presidentialBriefApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['PresidentialBrief'],
  endpoints: (builder) => ({
    getTodaysBrief: builder.query<PresidentialBrief, string | undefined>({
      query: (queryString) => {
        const base = 'api/presidential-brief/today/';
        return queryString ? `${base}?${queryString}` : base;
      },
      providesTags: ['PresidentialBrief'],
    }),
    submitThumbsDown: builder.mutation<
      PresidentialBrief,
      { reason?: string }
    >({
      query: (body) => ({
        url: 'api/presidential-brief/thumbs-down/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PresidentialBrief'],
    }),
  }),
});

export const { useGetTodaysBriefQuery, useSubmitThumbsDownMutation } =
  presidentialBriefApi;
